import React, { useEffect, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList,
  Legend
} from 'recharts';
import { IconButton } from '@mui/material';
import { PrintOutlined } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import Loading from 'components/Loading';
import './AppDataTable.css';
import SpinLoader from 'components/Loader/SpinLoader';
import SentryWrap from 'components/Sentry';
import styled from 'styled-components';


const ComparisonReport = ({ reportByYear, fetchReportByYear, loader }) => {
  const printRef = useRef(null);
  const [printLoader, setPrintLoader] = useState(false);

  useEffect(() => {
    if (reportByYear === null || reportByYear?.length === 0) {
      fetchReportByYear();
    }
  }, []);

  const printCall = useReactToPrint({
    contentRef: printRef,
    onBeforeGetContent: async () => {
      setPrintLoader(true);
      if (printRef.current) {
        printRef.current.style.height = 'auto';
        printRef.current.style.overflow = 'visible';
      }
    },
    onBeforePrint: async () => {
      setPrintLoader(false);
      if (printRef.current) {
        printRef.current.style.height = '';
        printRef.current.style.overflow = '';
      }
    },
  });
  const skillMapping = {
    speaking: 'Speaking',
    listening: 'Listening',
    reading: 'Reading',
    writing: 'Writing',
    comprehension: 'Comprehension',
    oral: 'Vocal',
    literacy: 'Literacy',
    productive: 'Active'
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };


  const currentSession = reportByYear?.currentSession || {};
  const previousSession = reportByYear?.previousSession || {};

  const noPreviousYearSubmissions =
    reportByYear != null &&
    !loader &&
    (Number(previousSession?.number_of_submissions) === 0) ||
    (Number(currentSession?.number_of_submissions) === 0);

  const allCurrentPointsZero = Object.keys(skillMapping).every(skillKey => {
    const currentSkill = currentSession[skillKey] || {};
    const currentScore = currentSkill.score || {};
    const currentPercentage = currentScore.percantage_points_obtained || 0;
    return currentPercentage === 0;
  });

  const data = Object.keys(skillMapping).map(skillKey => {
    const currentSkill = currentSession[skillKey] || {};
    const previousSkill = previousSession[skillKey] || {};
    const currentScore = currentSkill.score || {};
    const previousScore = previousSkill.score || {};

    const currentPercentage = currentScore.percantage_points_obtained || 0;
    const previousPercentage = previousScore.percantage_points_obtained || 0;
    const diffPercentage = allCurrentPointsZero ? 0 : (currentPercentage - previousPercentage);

    const currentTestDate = formatDate(currentSession.testDate || '');
    const previousTestDate = formatDate(previousSession.testDate || '');

    return {
      group: skillMapping[skillKey],
      testDate1: previousTestDate || previousSession.session || '',
      numberTested1: diffPercentage === 0 ? 0 : previousSession.number_of_submissions + currentSession.number_of_submissions || 0,
      testDate2: currentTestDate || currentSession.session || '',
      numberTested2: diffPercentage === 0 ? 0 : previousSession.number_of_submissions + currentSession.number_of_submissions || 0,
      diffPercentage: diffPercentage,
      numberTested: diffPercentage === 0 ? 0 : previousSession.number_of_submissions + currentSession.number_of_submissions || 0
    };
  });

  const chartData = data.map((item, index) => ({
    group: item.group,
    value: item.diffPercentage,
    numberTested: item.numberTested,
    testDate1: item.testDate1,
    numberTested1: item.numberTested1,
    testDate2: item.testDate2,
    numberTested2: item.numberTested2
  }));

  const CustomLabel = (props) => {
    const { x, y, width, value } = props;
    if (value === 0) return null;

    const labelText = `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
    const barWidth = Math.abs(width);

    if (barWidth > 50) {
      return (
        <text
          x={x + width / 2}
          y={y + 15}
          fill="#fff"
          fontSize="12"
          fontWeight="600"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {labelText}
        </text>
      );
    } else {
      const offsetX = width > 0 ? width + 5 : width - 5;
      return (
        <text
          x={x + offsetX}
          y={y + 15}
          fill="#333"
          fontSize="12"
          fontWeight="600"
          textAnchor={width > 0 ? 'start' : 'end'}
          dominantBaseline="middle"
        >
          {labelText}
        </text>
      );
    }
  };

  const renderCustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const firstPayload = payload.find(p => p.payload) || payload[0];
    const data = firstPayload?.payload;

    if (!data || !data.group) return null;

    return (
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        minWidth: '200px'
      }}>
        <div style={{ marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>
          {data.group}
        </div>
        <div style={{ marginBottom: '4px', fontSize: '12px' }}>
          Number Tested: {data.numberTested}
        </div>
        <div style={{ marginBottom: '4px', fontSize: '12px', color: data.value >= 0 ? '#70AD47' : '#FF6B6B' }}>
          Difference: {data.value > 0 ? '+' : ''}{data.value.toFixed(1)}%
        </div>
      </div>
    );
  };

  return (
    <SentryWrap>
      <div className="comparison-report-wrapper printable" ref={printRef} style={{ position: 'relative' }}>
        <div className="width-100 mt-10px">
          <SpinLoader dataloader={loader} />
        </div>
        {!noPreviousYearSubmissions && (
        <div className="comparison-print-button-container">
          <IconButton
            onClick={() => printCall()}
            className="comparison-print-button"
            aria-label="print"
          >
            {printLoader ? <Loading size={20} /> : <PrintOutlined />}
          </IconButton>
        </div>
        )}
        {noPreviousYearSubmissions ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              fontSize: '16px',
              color: '#64748b',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              margin: '20px 0',
            }}
          >
            There is no previous data to compare current data with.
          </div>
        ) : (
        <>
        <div className="comparison-report-container">
          <div className="comparison-report-table-section">
            <div className="comparison-report-header-row">
              <div className="comparison-header-group">Group</div>
              <div className="comparison-header-date">Test Session</div>
            </div>
            <div className="comparison-report-table-body">
              {data.map((item, index) => (
                <React.Fragment key={index}>
                  <div className="comparison-report-table-row">
                    <div className="comparison-cell-group">{item.group}</div>
                    <div className="comparison-cell-date">{currentSession.session}</div>
                  </div>
                  {/* <div className="comparison-report-table-row comparison-row-highlight">
                  <div className="comparison-cell-group"></div>
                  <div className="comparison-cell-date">{item.testDate2}</div>
                </div> */}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="comparison-report-chart-section">
            <div className="comparison-chart-title hideTextSet">Number Tested</div>
            <div className="comparison-chart-container">
              <div className="comparison-chart-area">
                <ResponsiveContainer width="100%" height={500} className="comparison-chart-responsive">
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 0, right: 50, left: 40, bottom: 50 }}
                    barCategoryGap="10%"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      domain={[-100, 100]}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <YAxis
                      type="category"
                      dataKey="group"
                      width={30}
                      tick={{ fontSize: 12 }}
                      axisLine={true}
                      tickFormatter={(value) => {
                        const item = chartData.find(d => d.group === value);
                        return item ? item.numberTested : value;
                      }}
                    />
                    <Tooltip
                      content={renderCustomTooltip}
                      cursor={{ fill: 'rgba(0,0,0,0.06)' }}
                    />
                    <Bar
                      dataKey="value"
                      fill="#4A90E2"
                      radius={[0, 4, 4, 0]}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill="#4A90E2" />
                      ))}
                      <LabelList content={CustomLabel} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
        {allCurrentPointsZero && !loader && (
          <div style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            padding: '8px 16px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#856404',
            fontWeight: '500',
            zIndex: 10
          }}>
            No submission available in current year
          </div>
        )}

        <HeadingTitle>Year Comparison</HeadingTitle>
        <GraphWithCurrentAndPreviousSessions reportByYear={reportByYear} />
        </>
        )}
      </div>
    </SentryWrap>
  );
};

export default ComparisonReport;

const GraphWithCurrentAndPreviousSessions = ({ reportByYear }) => {
  const currentSession = reportByYear?.currentSession || {};
  const previousSession = reportByYear?.previousSession || {};

  const skillMapping = {
    speaking: 'Speaking',
    listening: 'Listening',
    reading: 'Reading',
    writing: 'Writing',
    comprehension: 'Comprehension',
    oral: 'Vocal',
    literacy: 'Literacy',
    productive: 'Active'
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };

  const chartData = Object.keys(skillMapping).map(skillKey => {
    const currentSkill = currentSession[skillKey] || {};
    const previousSkill = previousSession[skillKey] || {};
    const currentScore = currentSkill.score || {};
    const previousScore = previousSkill.score || {};

    const currentPercentage = currentScore.percantage_points_obtained || 0;
    const previousPercentage = previousScore.percantage_points_obtained || 0;

    const currentTestDate = formatDate(currentSession.testDate || '');
    const previousTestDate = formatDate(previousSession.testDate || '');

    return {
      group: skillMapping[skillKey],
      currentSession: currentPercentage,
      previousSession: previousPercentage,
      currentTestDate: currentTestDate || currentSession.session || '',
      previousTestDate: previousTestDate || previousSession.session || '',
      currentNumberTested: currentSession.number_of_submissions || 0,
      previousNumberTested: previousSession.number_of_submissions || 0
    };
  });

  const CustomLabel = ({ x, y, width, value, dataKey }) => {
    if (value === 0) return null;

    const labelText = `${value.toFixed(1)}%`;
    const barWidth = Math.abs(width);

    // For vertical layout, width is the horizontal extent of the bar
    // Show label inside bar if bar is wide enough to fit text (at least 35px)
    if (barWidth > 35) {
      return (
        <text
          x={x + width / 2}
          y={y + 10}
          fill="#fff"
          fontSize="9"
          fontWeight="500"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {labelText}
        </text>
      );
    } else {
      // Show label outside bar if bar is too small
      const offsetX = width > 0 ? width + 5 : width - 5;
      return (
        <text
          x={x + offsetX}
          y={y + 10}
          fill="#333"
          fontSize="9"
          fontWeight="500"
          textAnchor={width > 0 ? 'start' : 'end'}
          dominantBaseline="middle"
        >
          {labelText}
        </text>
      );
    }
  };

  const renderCustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0]?.payload;
    if (!data || !data.group) return null;

    const currentValue = payload.find(p => p.dataKey === 'currentSession')?.value || 0;
    const previousValue = payload.find(p => p.dataKey === 'previousSession')?.value || 0;

    return (
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        minWidth: '200px'
      }}>
        <div style={{ marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>
          {data.group}
        </div>
        <div style={{ marginBottom: '4px', fontSize: '12px' }}>
          <span style={{ color: '#4A90E2', fontWeight: 600 }}>Current Session ({data.currentTestDate}):</span> {currentValue.toFixed(1)}%
        </div>
        <div style={{ marginBottom: '4px', fontSize: '12px' }}>
          <span style={{ color: '#70AD47', fontWeight: 600 }}>Previous Session ({data.previousTestDate}):</span> {previousValue.toFixed(1)}%
        </div>
        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0', fontSize: '12px' }}>
          Number Tested - Current: {data.currentNumberTested}, Previous: {data.previousNumberTested}
        </div>
      </div>
    );
  };

  const maxValue = Math.max(
    ...chartData.map(d => Math.max(d.currentSession, d.previousSession)),
    100
  );

  return (
    <div className="comparison-report-wrapper printable" style={{ position: 'relative', marginTop: '40px' }}>
      <div className="comparison-report-container">
        <div className="comparison-report-table-section">
          <div className="comparison-report-header-row">
            <div className="comparison-header-group">Group</div>
            <div className="comparison-header-date">Test Session</div>
          </div>
          <div className="comparison-report-table-body">
            {chartData.map((item, index) => (
              <React.Fragment key={index}>
                <div className="comparison-report-table-row">
                  <div className="comparison-cell-group">{item.group}</div>
                  <div className="comparison-cell-date">{item.currentTestDate}</div>
                </div>
                <div className="comparison-report-table-row comparison-row-highlight">
                  <div className="comparison-cell-group"></div>
                  <div className="comparison-cell-date">{item.previousTestDate}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="comparison-report-chart-section">
          <div className="comparison-chart-title" style={{ paddingLeft: '80px' }}>Percentage Points Obtained</div>
          <div className="comparison-chart-container">
            <div className="comparison-chart-area">
              <ResponsiveContainer width="100%" height={500} className="comparison-chart-responsive">
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 0, right: 50, left: 80, bottom: 50 }}
                  barCategoryGap="10%"
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    domain={[0, Math.ceil(maxValue / 10) * 10]}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <YAxis
                    type="category"
                    dataKey="group"
                    width={70}
                    tick={{ fontSize: 12 }}
                    axisLine={true}
                  />
                  <Tooltip
                    content={renderCustomTooltip}
                    cursor={{ fill: 'rgba(0,0,0,0.06)' }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="rect"
                    formatter={(value) => {
                      if (value === 'currentSession') {
                        return currentSession.session || 'Current Session';
                      }
                      return previousSession.session || 'Previous Session';
                    }}
                  />
                  <Bar
                    dataKey="currentSession"
                    fill="#4A90E2"
                    radius={[0, 4, 4, 0]}
                    name="currentSession"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-current-${index}`} fill="#4A90E2" />
                    ))}
                    <LabelList content={(props) => <CustomLabel {...props} dataKey="currentSession" />} />
                  </Bar>
                  <Bar
                    dataKey="previousSession"
                    fill="#70AD47"
                    radius={[0, 4, 4, 0]}
                    name="previousSession"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-previous-${index}`} fill="#70AD47" />
                    ))}
                    <LabelList content={(props) => <CustomLabel {...props} dataKey="previousSession" />} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const HeadingTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  padding: 12px 0;
  margin-bottom: 10px;
  text-align: center;
`;